import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

type PullState = 'idle' | 'pulling' | 'threshold-reached' | 'refreshing';

interface PullToRefreshProps {
  children: ReactNode;
  onRefresh: () => Promise<void>;
  disabled?: boolean;
  threshold?: number;
}

const MAX_PULL = 120;
const RESISTANCE = 0.4;
const DEFAULT_THRESHOLD = 65;
const RETURN_ANIMATION_MS = 300;

/**
 * Check if the touch target is inside a fixed-position overlay
 * (modal, drawer, confirmation dialog, sidebar overlay, etc.).
 */
function isInsideFixedOverlay(target: EventTarget | null): boolean {
  let el = target instanceof HTMLElement
    ? target
    : (target instanceof Node ? target.parentElement : null);

  while (el && el !== document.body) {
    const { position } = window.getComputedStyle(el);
    if (position === 'fixed') {
      return true;
    }
    el = el.parentElement;
  }
  return false;
}

/**
 * Walk up the DOM from the touch target to find the nearest scrollable ancestor.
 */
function findScrollableAncestor(target: EventTarget | null): HTMLElement | null {
  let el = target instanceof HTMLElement
    ? target
    : (target instanceof Node ? target.parentElement : null);

  while (el && el !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(el);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      el.scrollHeight > el.clientHeight
    ) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

interface TouchTrackingState {
  y: number;
  isTracking: boolean;
  scrollableEl: HTMLElement | null;
}

const IDLE_TOUCH: TouchTrackingState = { y: 0, isTracking: false, scrollableEl: null };

export default function PullToRefresh({
  children,
  onRefresh,
  disabled = false,
  threshold = DEFAULT_THRESHOLD,
}: PullToRefreshProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<PullState>('idle');
  const [pullDistance, setPullDistance] = useState(0);
  const touchRef = useRef<TouchTrackingState>(IDLE_TOUCH);

  // Controls the CSS transition on the content wrapper.
  // CRITICAL: a persistent transition on transform creates a CSS containing block,
  // which breaks position:fixed for all descendants (modals, drawers, etc.).
  // We only enable it for the brief return animation, then immediately clear it.
  const [animatingReturn, setAnimatingReturn] = useState(false);
  const returnTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Ref for the dynamically-added non-passive touchmove listener.
  // CRITICAL: a permanent non-passive touchmove on document forces the browser
  // to block scroll compositing while it waits for the handler, which breaks
  // native scrolling in modals/overlays after page refresh on mobile browsers.
  // The listener is only present during an active pull-down gesture.
  const moveListenerRef = useRef<((e: TouchEvent) => void) | null>(null);

  // Refs for prop values needed inside the dynamic touchmove handler closure
  const thresholdRef = useRef(threshold);
  thresholdRef.current = threshold;

  const isTouchDevice = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  const removeMoveListener = useCallback(() => {
    if (moveListenerRef.current) {
      document.removeEventListener('touchmove', moveListenerRef.current);
      moveListenerRef.current = null;
    }
  }, []);

  const resetToIdle = useCallback(() => {
    setAnimatingReturn(true);
    setState('idle');
    setPullDistance(0);
    touchRef.current = IDLE_TOUCH;
    removeMoveListener();

    if (returnTimerRef.current) clearTimeout(returnTimerRef.current);
    returnTimerRef.current = setTimeout(() => setAnimatingReturn(false), RETURN_ANIMATION_MS + 50);
  }, [removeMoveListener]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      removeMoveListener();
      if (returnTimerRef.current) clearTimeout(returnTimerRef.current);
    };
  }, [removeMoveListener]);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (disabled || state === 'refreshing') return;

      if (isInsideFixedOverlay(e.target)) {
        touchRef.current = IDLE_TOUCH;
        return;
      }

      const scrollableEl = findScrollableAncestor(e.target);
      const scrollTop = scrollableEl
        ? scrollableEl.scrollTop
        : (window.scrollY || document.documentElement.scrollTop || 0);

      const shouldTrack = scrollTop <= 5;

      touchRef.current = {
        y: e.touches[0].clientY,
        isTracking: shouldTrack,
        scrollableEl,
      };

      // Only add the non-passive touchmove when at the top of a scrollable area.
      // This is the ONLY time we might need to call preventDefault to intercept scroll.
      if (shouldTrack) {
        removeMoveListener();

        const handleMove = (moveEvent: TouchEvent) => {
          if (!touchRef.current.isTracking) return;

          const { y: startY, scrollableEl: scrollEl } = touchRef.current;
          const currentY = moveEvent.touches[0].clientY;
          const deltaY = currentY - startY;

          const currentScrollTop = scrollEl
            ? scrollEl.scrollTop
            : (window.scrollY || document.documentElement.scrollTop || 0);

          // User scrolled away from top — release control
          if (currentScrollTop > 5) {
            touchRef.current = IDLE_TOUCH;
            removeMoveListener();
            setState('idle');
            setPullDistance(0);
            return;
          }

          // Finger moving up (scrolling down) — let the browser handle it
          if (deltaY <= 0) {
            setState('idle');
            setPullDistance(0);
            return;
          }

          // Finger pulling down from the top — intercept native scroll
          moveEvent.preventDefault();

          const adjustedDelta = Math.min(deltaY * RESISTANCE, MAX_PULL);
          setPullDistance(adjustedDelta);
          setState(adjustedDelta >= thresholdRef.current ? 'threshold-reached' : 'pulling');
        };

        moveListenerRef.current = handleMove;
        document.addEventListener('touchmove', handleMove, { passive: false });
      }
    },
    [disabled, state, removeMoveListener]
  );

  const handleTouchEnd = useCallback(async () => {
    if (state === 'refreshing') return;

    if (state === 'threshold-reached') {
      setState('refreshing');
      setPullDistance(threshold);

      try {
        await onRefresh();
      } catch (error) {
        console.error('[PullToRefresh] Refresh failed:', error);
      } finally {
        resetToIdle();
      }
    } else {
      resetToIdle();
    }
  }, [state, threshold, onRefresh, resetToIdle]);

  useEffect(() => {
    if (!isTouchDevice || disabled) return;

    // Only passive listeners are permanent on document.
    // The non-passive touchmove is added/removed dynamically per gesture
    // so it never interferes with native scroll in modals or after page refresh.
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isTouchDevice, disabled, handleTouchStart, handleTouchEnd]);

  if (!isTouchDevice) {
    return <>{children}</>;
  }

  // Inline styles only applied when actively pulling or animating back.
  // NEVER during idle — avoids creating a containing block that breaks position:fixed.
  const contentStyle: React.CSSProperties = {};
  if (pullDistance > 0) {
    contentStyle.transform = `translateY(${pullDistance}px)`;
  }
  if (animatingReturn) {
    contentStyle.transition = `transform ${RETURN_ANIMATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;
  }

  return (
    <div ref={containerRef} className="relative flex-1 flex flex-col min-h-0">
      <MaterialPullIndicator
        state={state}
        pullDistance={pullDistance}
        threshold={threshold}
      />

      <div className="flex-1 flex flex-col min-h-0" style={contentStyle}>
        {children}
      </div>
    </div>
  );
}

interface MaterialPullIndicatorProps {
  state: PullState;
  pullDistance: number;
  threshold: number;
}

function MaterialPullIndicator({ state, pullDistance, threshold }: MaterialPullIndicatorProps) {
  const progress = Math.min(pullDistance / threshold, 1);

  if (state === 'idle' && pullDistance === 0) {
    return null;
  }

  const size = 40;
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const strokeDashoffset = circumference * (1 - progress);
  const scale = 0.5 + progress * 0.5;
  const opacity = Math.min(progress * 1.2, 1);
  const topPosition = Math.max(pullDistance - 30, 20);

  return (
    <div
      className="fixed left-1/2 z-50 pointer-events-none"
      style={{
        // Offset by safe-area-inset-top so the spinner clears the iOS notch.
        // With viewport-fit=cover + Capacitor contentInset=automatic, a raw
        // top: 20 renders behind the status bar on iPhones with a notch.
        top: `calc(${topPosition}px + env(safe-area-inset-top, 0px))`,
        transform: `translateX(-50%) scale(${scale})`,
        opacity,
        transition: state === 'idle' ? 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
      }}
    >
      <div
        className="flex items-center justify-center rounded-full bg-white shadow-md"
        style={{
          width: size + 16,
          height: size + 16,
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
        }}
      >
        {state === 'refreshing' ? (
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            className="animate-spin"
            style={{ animationDuration: '1s' }}
          >
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth={strokeWidth}
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#2563eb"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * 0.75}
              style={{
                transformOrigin: 'center',
              }}
            />
          </svg>
        ) : (
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            style={{
              transform: 'rotate(-90deg)',
            }}
          >
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth={strokeWidth}
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={progress >= 1 ? '#16a34a' : '#2563eb'}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              style={{
                transition: 'stroke-dashoffset 0.1s ease-out, stroke 0.2s ease',
              }}
            />
            {progress >= 1 && (
              <g transform={`translate(${size / 2 - 8}, ${size / 2 - 8}) rotate(90, 8, 8)`}>
                <path
                  d="M6 10l2 2 4-4"
                  fill="none"
                  stroke="#16a34a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            )}
          </svg>
        )}
      </div>

      <div
        className="text-center mt-2 text-xs font-medium"
        style={{
          color: state === 'threshold-reached' ? '#16a34a' : '#6b7280',
          opacity: progress > 0.3 ? 1 : 0,
          transition: 'opacity 0.2s ease',
        }}
      >
        {state === 'refreshing'
          ? 'Refreshing...'
          : state === 'threshold-reached'
          ? 'Release to refresh'
          : 'Pull to refresh'}
      </div>
    </div>
  );
}

/**
 * Global pull-to-refresh wrapper for the main content area.
 * Use this at the app level to enable pull-to-refresh globally.
 */
export function GlobalPullToRefresh({
  children,
  onRefresh,
  disabled = false,
}: {
  children: ReactNode;
  onRefresh: () => Promise<void>;
  disabled?: boolean;
}) {
  return (
    <PullToRefresh onRefresh={onRefresh} disabled={disabled}>
      {children}
    </PullToRefresh>
  );
}
